import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useStockMonitoringRealtime = () => {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    // Subscribe to notification_product_monitoring changes
    const monitoringChannel = supabase
      .channel('stock-monitoring-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_product_monitoring'
        },
        (payload) => {
          const data = payload.new || payload.old;
          if (data && 'notification_id' in data && data.notification_id) {
            queryClient.invalidateQueries({ 
              queryKey: ['stock-status', data.notification_id] 
            });
          }
        }
      )
      .subscribe();

    // Subscribe to notification_inventory_checks changes
    const inventoryChannel = supabase
      .channel('inventory-checks-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_inventory_checks'
        },
        async (payload) => {
          const data = payload.new || payload.old;
          if (data && 'monitoring_id' in data && data.monitoring_id) {
            const { data: monitoring } = await supabase
              .from('notification_product_monitoring')
              .select('notification_id')
              .eq('id', data.monitoring_id as string)
              .single();
            
            if (monitoring?.notification_id) {
              queryClient.invalidateQueries({ 
                queryKey: ['stock-status', monitoring.notification_id] 
              });
            }
          }
        }
      )
      .subscribe();

    // Subscribe to voorraad_live changes to keep stock badges up-to-date
    const voorraadChannel = supabase
      .channel('voorraad-stock-monitoring')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'voorraad_live'
        },
        async (payload) => {
          const data = (payload.new || payload.old) as { product_id?: string } | null;
          if (data?.product_id) {
            // Find which notifications monitor this SKU
            const { data: monitoringRows } = await supabase
              .from('notification_product_monitoring')
              .select('notification_id')
              .eq('product_sku', data.product_id)
              .eq('is_monitored', true);

            if (monitoringRows && monitoringRows.length > 0) {
              const uniqueIds = [...new Set(monitoringRows.map(r => r.notification_id))];
              for (const nId of uniqueIds) {
                queryClient.invalidateQueries({ queryKey: ['stock-status', nId] });
              }
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(monitoringChannel);
      supabase.removeChannel(inventoryChannel);
      supabase.removeChannel(voorraadChannel);
    };
  }, [queryClient]);
};
